/**
 * Page for accepting vault invitations
 */

import { useNavigate, useParams } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAcceptInvitation, useValidateInvitation } from "../hooks/useInvitations";
import LoadingSpinner from "../components/Common/LoadingSpinner";
import Header from "../components/Common/Header";

export default function AcceptInvitation() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect, isLoading: authLoading } = useAuth0();

  const { data: validation, isLoading: validating, error: validationError } =
    useValidateInvitation(token || null);
  const acceptMutation = useAcceptInvitation();

  // Redirect to login if not authenticated
  const handleLogin = () => {
    loginWithRedirect({
      appState: { returnTo: `/invite/${token}` },
    });
  };

  // Handle accept invitation
  const handleAccept = async () => {
    if (!token) return;

    try {
      const result = await acceptMutation.mutateAsync(token);
      // Show success and redirect to dashboard
      navigate("/dashboard", {
        state: {
          message: `Welcome to ${result.vault_name}! You've been added as a ${
            result.role === "VAULT_CO_ADMIN" ? "Co-Admin" : "Member"
          }.`,
        },
      });
    } catch (err) {
      console.error("Failed to accept invitation:", err);
    }
  };

  // Loading state
  if (authLoading || validating) {
    return (
      <div className="min-h-screen bg-vault-50 flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Validation error
  if (validationError || !validation) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-red-200 bg-white p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-6 w-6 text-red-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="mt-4 font-serif text-2xl font-semibold text-vault-900">
              Invalid Invitation
            </h2>
            <p className="mt-2 text-vault-600">
              This invitation link is not valid or has expired.
            </p>
            <button
              onClick={() => navigate("/")}
              className="mt-6 rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700"
            >
              Go to Home
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Invalid invitation
  if (!validation.valid) {
    return (
      <div className="min-h-screen bg-vault-50">
        <Header />
        <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="rounded-lg border border-red-200 bg-white p-8 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={2}
                stroke="currentColor"
                className="h-6 w-6 text-red-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>
            <h2 className="mt-4 font-serif text-2xl font-semibold text-vault-900">
              Invitation Error
            </h2>
            <p className="mt-2 text-vault-600">{validation.error}</p>
            <button
              onClick={() => navigate("/")}
              className="mt-6 rounded-md bg-vault-600 px-4 py-2 text-sm font-medium text-white hover:bg-vault-700"
            >
              Go to Home
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Valid invitation
  return (
    <div className="min-h-screen bg-vault-50">
      <Header />
      <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="rounded-lg border border-vault-200 bg-white p-8 shadow-sm">
          {/* Icon */}
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-vault-100">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="h-8 w-8 text-vault-600"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
          </div>

          {/* Title */}
          <h1 className="mt-6 text-center font-serif text-3xl font-bold text-vault-900">
            You&apos;ve Been Invited!
          </h1>

          {/* Invitation Details */}
          <div className="mt-6 space-y-3 rounded-lg bg-vault-50 p-4">
            <div className="flex justify-between text-sm">
              <span className="text-vault-600">Vault:</span>
              <span className="font-medium text-vault-900">{validation.vault_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-vault-600">Invited by:</span>
              <span className="font-medium text-vault-900">{validation.inviter_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-vault-600">Role:</span>
              <span className="font-medium text-vault-900">
                {validation.role === "VAULT_CO_ADMIN" ? "Co-Admin" : "Member"}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-vault-600">Expires:</span>
              <span className="font-medium text-vault-900">
                {validation.expires_at
                  ? new Date(validation.expires_at).toLocaleDateString()
                  : "N/A"}
              </span>
            </div>
          </div>

          {/* Description */}
          <p className="mt-6 text-center text-sm text-vault-600">
            {validation.role === "VAULT_CO_ADMIN"
              ? "As a Co-Admin, you'll have full management access to this vault including adding members and managing settings."
              : "As a Member, you'll be able to view and contribute to this vault's family history."}
          </p>

          {/* Action Buttons */}
          <div className="mt-8">
            {isAuthenticated ? (
              <button
                onClick={handleAccept}
                disabled={acceptMutation.isPending}
                className="w-full rounded-md bg-vault-600 px-4 py-3 text-base font-medium text-white hover:bg-vault-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {acceptMutation.isPending ? (
                  <>
                    <LoadingSpinner size="sm" />
                    Accepting...
                  </>
                ) : (
                  "Accept Invitation"
                )}
              </button>
            ) : (
              <>
                <button
                  onClick={handleLogin}
                  className="w-full rounded-md bg-vault-600 px-4 py-3 text-base font-medium text-white hover:bg-vault-700 transition-colors"
                >
                  Sign Up or Log In to Accept
                </button>
                <p className="mt-4 text-center text-xs text-vault-500">
                  You need to create an account or log in to accept this invitation
                </p>
              </>
            )}
          </div>

          {/* Error Message */}
          {acceptMutation.isError && (
            <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700">
              {acceptMutation.error instanceof Error
                ? acceptMutation.error.message
                : "Failed to accept invitation. Please try again."}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
